import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { storiesApi } from '../api/stories';
import usePlotTrack from '../hooks/usePlotTrack';
import { useSyncStoryContext } from '../hooks/useSyncStoryContext';
import PlotTrackLayout from '../components/PlotTrack/PlotTrackLayout';
import StoryPicker from '../components/Common/StoryPicker';
import {
  PLOTTRACK_ERROR_STORY_MISSING,
  classifyPlotTrackLoadError,
} from '../utils/plottrackLoadError';
import { plottrackPath } from '../utils/plottrackPath';
import { forgetOpenProject } from '../utils/lastProject';
import '../components/PlotTrack/PlotTrackLayout.css';

const unwrapList = (res) => (Array.isArray(res) ? res : res?.data || []);

const OPENING_SLOW_MS = 8000;

const byRecency = (a, b) => {
  const at = new Date(a.last_activity_at || a.created_at).getTime();
  const bt = new Date(b.last_activity_at || b.created_at).getTime();
  return bt - at;
};

/**
 * PlotTrackPage — writer-facing outline door (#817).
 * Route: /plottrack and /plottrack/:storyId.
 * Story resolution: the book you open is the grid you see (#849).
 * Bare /plottrack keeps the book already in context. Most-recent is
 * only when no book is open. Switch story stays.
 */
export default function PlotTrackPage() {
  const { storyId: routeStoryId } = useParams();
  const { currentProject, currentStory, setProjectAndStory, storyListVersion } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const startGenerate = (
    location.state?.startGenerate === true
    || searchParams.get('handoff') === 'generate'
  );

  // URL is canonical. Bare /plottrack still opens the book you were on —
  // not the most recently active sibling (#849). Pass a value (including
  // null) so board mode stays on and currentStory can fill the grid.
  const openedId = routeStoryId || currentStory?.id || null;
  const { storyId, loadedStory } = useSyncStoryContext({ routeStoryId: openedId });
  const board = usePlotTrack(storyId);
  const storyMissing = board.error
    && classifyPlotTrackLoadError(board.error).kind === PLOTTRACK_ERROR_STORY_MISSING;

  const [pickerOpen, setPickerOpen] = useState(false);
  const [projectStories, setProjectStories] = useState(null);
  const [projectStoriesLoading, setProjectStoriesLoading] = useState(false);
  const [projectStoriesRefreshNonce, setProjectStoriesRefreshNonce] = useState(0);
  const [openingSlow, setOpeningSlow] = useState(false);
  const refreshProjectStories = useCallback(() => {
    setProjectStoriesRefreshNonce((n) => n + 1);
  }, []);

  const activeProjectId = currentProject?.id || loadedStory?.project_id || null;

  useEffect(() => {
    if (!storyMissing) return;
    forgetOpenProject();
    navigate('/projects', { replace: true });
  }, [storyMissing, navigate]);

  useEffect(() => {
    if (!activeProjectId) { setProjectStories(null); return undefined; }
    let cancelled = false;
    setProjectStoriesLoading(true);
    storiesApi.list(activeProjectId)
      .then((res) => { if (!cancelled) setProjectStories(unwrapList(res)); })
      .catch(() => { if (!cancelled) setProjectStories([]); })
      .finally(() => { if (!cancelled) setProjectStoriesLoading(false); });
    return () => { cancelled = true; };
  }, [activeProjectId, storyListVersion, projectStoriesRefreshNonce]);

  useEffect(() => {
    if (board.error) return;
    if (routeStoryId) { setPickerOpen(false); return; }
    if (currentStory?.id) {
      setPickerOpen(false);
      navigate(plottrackPath(currentStory.id), { replace: true });
      return;
    }
    if (!currentProject) { setPickerOpen(true); return; }
    if (!projectStories || projectStories.length === 0) return;
    const mostRecent = [...projectStories].sort(byRecency)[0];
    setProjectAndStory(currentProject, mostRecent);
    navigate(plottrackPath(mostRecent.id), { replace: true });
  }, [
    routeStoryId,
    currentStory?.id,
    currentProject,
    projectStories,
    navigate,
    setProjectAndStory,
    board.error,
  ]);

  const storyName = loadedStory?.name || null;
  const waitingForBook = !storyId && Boolean(currentProject) && (
    projectStoriesLoading || projectStories === null || (projectStories && projectStories.length > 0)
  );
  const waitingForBoard = Boolean(storyId) && Boolean(board.isLoading);
  const opening = waitingForBook || waitingForBoard;

  useEffect(() => {
    if (!opening) {
      setOpeningSlow(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setOpeningSlow(true), OPENING_SLOW_MS);
    return () => window.clearTimeout(timer);
  }, [opening, storyId]);

  const seriesKnown = currentProject && currentProject.id === activeProjectId
    ? Boolean(currentProject.is_series)
    : undefined;

  const handlePick = useCallback((project, story) => {
    setProjectAndStory(project, story);
    setPickerOpen(false);
    navigate(plottrackPath(story.id));
  }, [setProjectAndStory, navigate]);

  const handleSwitchStory = useCallback((story) => {
    if (currentProject) setProjectAndStory(currentProject, story);
    navigate(plottrackPath(story.id));
  }, [currentProject, setProjectAndStory, navigate]);

  if (!storyId) {
    if (currentProject) {
      if (projectStoriesLoading || projectStories === null || projectStories.length > 0) {
        return (
          <div className="pt-page pt-page--state">
            <p>{openingSlow ? 'Still opening Plot Grid.' : 'Opening Plot Grid…'}</p>
            {openingSlow && (
              <button
                type="button"
                className="cor-btn cor-btn--accept"
                onClick={() => {
                  setOpeningSlow(false);
                  refreshProjectStories();
                }}
              >
                Try again
              </button>
            )}
          </div>
        );
      }
      return (
        <div className="pt-page">
          <div className="pt-page--state">
            <h2>No stories yet</h2>
            <p>{currentProject.name || 'This project'} doesn't have any stories yet.</p>
          </div>
        </div>
      );
    }
    return (
      <div className="pt-page">
        <div className="pt-page--state">
          <button className="cor-btn cor-btn--accept" onClick={() => setPickerOpen(true)}>
            Choose a story
          </button>
        </div>
        <StoryPicker
          isOpen={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onPick={handlePick}
          currentStoryId={storyId}
          title="Open a story on Plot Grid"
        />
      </div>
    );
  }

  if (board.isLoading) {
    return (
      <div className="pt-page pt-page--state">
        <p>{openingSlow ? 'Still opening Plot Grid.' : 'Opening Plot Grid…'}</p>
        {openingSlow && (
          <button
            type="button"
            className="cor-btn cor-btn--accept"
            onClick={() => {
              setOpeningSlow(false);
              board.reload?.();
            }}
          >
            Try again
          </button>
        )}
      </div>
    );
  }

  if (board.error) {
    if (storyMissing) {
      return <div className="pt-page" />;
    }
    const loadError = classifyPlotTrackLoadError(board.error);
    return (
      <div className="pt-page">
        <div className="pt-page--state">
          <h2>{loadError.title}</h2>
          {loadError.detail && <p>{loadError.detail}</p>}
          <button className="cor-btn cor-btn--accept" onClick={() => board.reload()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-page">
      <PlotTrackLayout
        storyName={storyName}
        board={board}
        storyId={storyId}
        currentStoryId={storyId}
        projectId={activeProjectId}
        isSeries={seriesKnown}
        startGenerate={startGenerate}
        handoffId={startGenerate ? location.key : ''}
        projectStories={projectStories}
        onSwitchStory={handleSwitchStory}
        onRefreshProjectStories={refreshProjectStories}
      />
      {!currentProject && (
        <StoryPicker
          isOpen={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onPick={handlePick}
          currentStoryId={storyId}
          title="Open a story on Plot Grid"
        />
      )}
    </div>
  );
}
